import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ApiService from '../../api/ApiService';
import LocalDataService from '../../api/LocalDataService';
import LoginModal from './LoginModal';

const AuthContext = createContext(null);

const getCurrentPath = () => {
  const hashPath = window.location.hash.replace(/^#/, '');
  return hashPath || '/';
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => LocalDataService.load_user_data());
  const [loginRequest, setLoginRequest] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminPermissionLoading, setAdminPermissionLoading] = useState(Boolean(user?.token));

  const openLogin = useCallback((redirectPath = getCurrentPath()) => {
    setLoginRequest({ redirectPath: redirectPath || '/' });
  }, []);

  const closeLogin = useCallback(() => setLoginRequest(null), []);

  const completeLogin = useCallback((userInfo) => {
    LocalDataService.set_user_data(userInfo);
    localStorage.removeItem('redirectAfterLogin');
    setUser(userInfo);
    setLoginRequest(null);
  }, []);

  const updateUser = useCallback((updates) => {
    setUser((currentUser) => {
      if (!currentUser) return currentUser;
      const nextUser = { ...currentUser, ...updates };
      LocalDataService.set_user_data(nextUser);
      return nextUser;
    });
  }, []);

  const logout = useCallback(() => {
    LocalDataService.set_user_data(null);
    setUser(null);
    setIsAdmin(false);
    setAdminPermissionLoading(false);
    setLoginRequest(null);
  }, []);

  useEffect(() => {
    let active = true;

    const loadAdminPermission = async () => {
      if (!user?.token) {
        setIsAdmin(false);
        setAdminPermissionLoading(false);
        return;
      }

      setAdminPermissionLoading(true);
      try {
        const response = await new ApiService().check_admin_permission(user.token);
        if (!active) return;
        setIsAdmin(Boolean(response?.code === 200 && response?.data?.is_admin));
      } catch (_) {
        if (active) setIsAdmin(false);
      } finally {
        if (active) setAdminPermissionLoading(false);
      }
    };

    loadAdminPermission();
    return () => {
      active = false;
    };
  }, [user?.token]);

  useEffect(() => {
    const handleAuthRequired = (event) => {
      setUser(null);
      openLogin(event?.detail?.redirectPath || getCurrentPath());
    };

    window.addEventListener('canvas:auth-required', handleAuthRequired);
    return () => window.removeEventListener('canvas:auth-required', handleAuthRequired);
  }, [openLogin]);

  const value = useMemo(() => ({
    user,
    isAuthenticated: Boolean(user?.token),
    isAdmin,
    adminPermissionLoading,
    openLogin,
    closeLogin,
    completeLogin,
    updateUser,
    logout
  }), [
    user,
    isAdmin,
    adminPermissionLoading,
    openLogin,
    closeLogin,
    completeLogin,
    updateUser,
    logout
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
      <LoginModal
        open={Boolean(loginRequest)}
        redirectPath={loginRequest?.redirectPath}
        onClose={closeLogin}
        onSuccess={completeLogin}
      />
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}

export default AuthContext;
